/**
 * Commands Tool Page
 *
 * Terminal serial interativo: o usuário conecta a um dispositivo, envia qualquer comando
 * livre e acompanha em tempo real tudo o que o dispositivo responde.
 * Tool color: emerald
 *
 * Workflow:
 * 1. Conectar à porta serial (botão Conectar)
 * 2. Enviar comandos arbitrários pelo campo de texto
 * 3. Acompanhar o stream de respostas no terminal em tempo real
 */

import { Loader2, Plug, PlugZap, Send, Terminal } from 'lucide-react';
import * as React from 'react';
import { ConsoleDisplay } from '@/components/production/ConsoleDisplay';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BAUD_RATE_OPTIONS,
  DEFAULT_BAUD_RATE,
  DEFAULT_STOP_BITS,
  STOP_BITS_OPTIONS,
  type TerminalLine,
  useSerialTerminal,
} from '@/hooks/production/useSerialTerminal';

// Prefixo visual de cada tipo de linha no terminal. O ConsoleDisplay renderiza string[]
// com cor única, então o prefixo é o que distingue enviado/recebido/info/erro.
const LINE_PREFIX: Record<TerminalLine['kind'], string> = {
  sent: '> ',
  received: '← ',
  info: '• ',
  error: '✗ ',
};

export default function CommandsPage() {
  const [terminalLines, setTerminalLines] = React.useState<string[]>([]);
  const [commandInput, setCommandInput] = React.useState('');
  const [baudRate, setBaudRate] = React.useState<number>(DEFAULT_BAUD_RATE);
  const [stopBits, setStopBits] = React.useState<1 | 2>(DEFAULT_STOP_BITS);
  // Histórico de comandos enviados, navegável com ↑/↓ (como num terminal real).
  const historyRef = React.useRef<string[]>([]);
  const historyIndexRef = React.useRef<number>(-1);

  const handleLine = React.useCallback((line: TerminalLine) => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setTerminalLines((prev) => [...prev, `[${timestamp}] ${LINE_PREFIX[line.kind]}${line.text}`]);
  }, []);

  const { isSupported, isConnected, isConnecting, connect, disconnect, sendCommand } =
    useSerialTerminal({ onLine: handleLine });

  const clearTerminal = React.useCallback(() => {
    setTerminalLines([]);
  }, []);

  // Wrapper para não passar o evento de clique como options ao connect.
  const handleConnect = React.useCallback(() => {
    void connect({ baudRate, stopBits });
  }, [connect, baudRate, stopBits]);

  const handleSend = React.useCallback(async () => {
    const command = commandInput.trim();
    if (!command) return;
    historyRef.current.push(command);
    historyIndexRef.current = historyRef.current.length;
    setCommandInput('');
    await sendCommand(command);
  }, [commandInput, sendCommand]);

  // ↑/↓ navega o histórico; Enter envia.
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        void handleSend();
        return;
      }
      const history = historyRef.current;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (history.length === 0) return;
        historyIndexRef.current = Math.max(0, historyIndexRef.current - 1);
        setCommandInput(history[historyIndexRef.current] ?? '');
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (history.length === 0) return;
        historyIndexRef.current = Math.min(history.length, historyIndexRef.current + 1);
        setCommandInput(history[historyIndexRef.current] ?? '');
      }
    },
    [handleSend],
  );

  return (
    <div className="container mx-auto pb-8 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-base font-semibold leading-7 text-gray-900">Terminal Serial</h1>
        <p className="mt-2 text-sm text-gray-700">
          Envie qualquer comando para o dispositivo e acompanhe as respostas em tempo real.
        </p>
      </div>

      {!isSupported && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          A Web Serial API não é suportada neste navegador. Use o Google Chrome ou o Microsoft Edge.
        </div>
      )}

      {/* Step 1: Connection */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge className="w-8 h-8 rounded-full flex items-center justify-center p-0 bg-black text-white">
              1
            </Badge>
            <div className="flex items-center gap-2">
              <Plug className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Conexão</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Conecte-se à porta serial do dispositivo para iniciar a sessão.
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label htmlFor="baud-rate">Baud rate</Label>
              <Select
                value={String(baudRate)}
                onValueChange={(value) => setBaudRate(Number(value))}
                disabled={isConnected || isConnecting}
              >
                <SelectTrigger id="baud-rate" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BAUD_RATE_OPTIONS.map((rate) => (
                    <SelectItem key={rate} value={String(rate)}>
                      {rate}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="stop-bits">Stop bits</Label>
              <Select
                value={String(stopBits)}
                onValueChange={(value) => setStopBits(Number(value) as 1 | 2)}
                disabled={isConnected || isConnecting}
              >
                <SelectTrigger id="stop-bits" className="w-28">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STOP_BITS_OPTIONS.map((bits) => (
                    <SelectItem key={bits} value={String(bits)}>
                      {bits}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {isConnected ? (
              <Button variant="destructive" onClick={disconnect} className="min-w-35">
                <PlugZap className="mr-2 h-4 w-4" />
                Desconectar
              </Button>
            ) : (
              <Button
                onClick={handleConnect}
                disabled={isConnecting || !isSupported}
                className="min-w-35 bg-black hover:bg-gray-800 text-white"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                    Conectando
                  </>
                ) : (
                  <>
                    <Plug className="mr-2 h-4 w-4" />
                    Conectar
                  </>
                )}
              </Button>
            )}
            <Badge variant={isConnected ? 'success-subtle' : 'secondary-subtle'} className="gap-1">
              <span
                className={`h-2 w-2 rounded-full ${isConnected ? 'bg-green-500' : 'bg-gray-400'}`}
              />
              {isConnected ? 'Conectado' : 'Desconectado'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Command Input */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              className={`w-8 h-8 rounded-full flex items-center justify-center p-0 text-white ${
                isConnected ? 'bg-black' : 'bg-gray-400'
              }`}
            >
              2
            </Badge>
            <div className="flex items-center gap-2">
              <Send className="h-5 w-5 text-black" />
              <CardTitle className="text-xl">Envio de Comando</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Digite o comando e pressione Enter. Use ↑ / ↓ para navegar pelo histórico.
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11">
          <div className="flex gap-3">
            <Input
              placeholder={isConnected ? 'Digite um comando (ex: CHECK)' : 'Conecte-se primeiro'}
              value={commandInput}
              onChange={(e) => setCommandInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!isConnected}
              className="font-mono"
            />
            <Button
              onClick={handleSend}
              disabled={!isConnected || !commandInput.trim()}
              className="min-w-28 bg-black hover:bg-gray-800 text-white"
            >
              <Send className="mr-2 h-4 w-4" />
              Enviar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Terminal */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center gap-3">
            <Badge
              className={`w-8 h-8 rounded-full flex items-center justify-center p-0 text-white ${
                terminalLines.length > 0 ? 'bg-black' : 'bg-gray-400'
              }`}
            >
              3
            </Badge>
            <div className="flex items-center gap-2">
              <Terminal
                className={`h-5 w-5 ${terminalLines.length > 0 ? 'text-black' : 'text-muted-foreground'}`}
              />
              <CardTitle className="text-xl">Terminal</CardTitle>
            </div>
          </div>
          <CardDescription className="ml-11">
            Respostas do dispositivo em tempo real.
          </CardDescription>
        </CardHeader>
        <CardContent className="ml-11">
          <ConsoleDisplay
            logs={terminalLines}
            height={400}
            title="Terminal Serial"
            textColor="green"
            bgColor="black"
            onClear={clearTerminal}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export { CommandsPage };
